/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-13
 */

import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';

const SkipLinkButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: -40,
  left: 6,
  zIndex: 9999,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: 600,
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: 4,
  textDecoration: 'none',
  transition: 'top 0.2s ease-in-out',
  '&:focus': {
    top: 6,
    outline: `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: 2,
  },
  '&:hover:focus': {
    top: 6,
    backgroundColor: theme.palette.primary.dark,
  },
}));

/**
 * Skip navigation link for keyboard accessibility.
 * Allows keyboard users to skip directly to main content.
 *
 * @param props - Component props
 * @param props.targetId - ID of the main content element to skip to
 * @returns Skip link component
 */
interface SkipLinkProps {
  targetId?: string;
}

const SkipLink: React.FC<SkipLinkProps> = ({ targetId = 'main-content' }) => {
  const handleSkip = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <SkipLinkButton
      onClick={handleSkip}
      aria-label="Skip to main content"
      tabIndex={0}
    >
      Skip to main content
    </SkipLinkButton>
  );
};

export default SkipLink;
